import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../config/axios';
import { AuthContext } from '../context/AuthContext';
import { formatError } from '../utils/errorHandler';
import { formatTrialEndDate } from '../utils/planAccess';
import { formatRoleLabel } from '../utils/roleLabels';
import UserAvatar from '../components/UserAvatar';
import CreateCompanyModal from '../components/CreateCompanyModal';
import Modal from '../components/Modal';
import PendingInvitationsTable from '../components/PendingInvitationsTable';
import EmptyState from '../components/EmptyState';
import LoadingSpinner from '../components/LoadingSpinner';
import { useConfirm } from '../context/ConfirmContext';
import './SuperAdminView.css';

const SuperAdminView = () => {
  const { t } = useTranslation(['companies', 'superAdmin', 'common', 'users', 'company']);
  const confirm = useConfirm();
  const navigate = useNavigate();
  const { user, fetchUser } = useContext(AuthContext);
  const [openingSupport, setOpeningSupport] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [usersWithoutCompany, setUsersWithoutCompany] = useState([]);
  const [taskCounts, setTaskCounts] = useState({ active_tasks: 0, done_tasks: 0 });
  const [loading, setLoading] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [error, setError] = useState('');
  const [showCreateCompanyModal, setShowCreateCompanyModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('employee');
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');
  const [trialPlan, setTrialPlan] = useState('professional');
  const [trialEndsAt, setTrialEndsAt] = useState('');
  const [trialError, setTrialError] = useState('');
  const [trialSuccess, setTrialSuccess] = useState('');
  const [trialSaving, setTrialSaving] = useState(false);
  const [supportWebhookUrl, setSupportWebhookUrl] = useState('');
  const [supportWebhookConfigured, setSupportWebhookConfigured] = useState(false);
  const [supportWebhookUsesEnv, setSupportWebhookUsesEnv] = useState(false);
  const [platformSettingsSaving, setPlatformSettingsSaving] = useState(false);
  const [platformSettingsError, setPlatformSettingsError] = useState('');
  const [platformSettingsSuccess, setPlatformSettingsSuccess] = useState('');
  const latestCompanyRequestRef = useRef(0);

  useEffect(() => {
    if (user?.role === 'super_admin') {
      fetchCompanies();
      fetchUsersWithoutCompany();
      fetchPlatformSettings();
    }
  }, [user]);

  const fetchPlatformSettings = async () => {
    try {
      const response = await axios.get('/api/platform/settings');
      const data = response.data.data || {};
      setSupportWebhookUrl(data.supportSlackWebhookOverride || '');
      setSupportWebhookConfigured(Boolean(data.supportSlackWebhookConfigured));
      setSupportWebhookUsesEnv(Boolean(data.supportSlackWebhookUsesEnvDefault));
      setPlatformSettingsError('');
    } catch (settingsError) {
      setPlatformSettingsError(formatError(settingsError) || t('superAdmin:failedSavePlatformSettings'));
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await axios.get('/api/companies');
      setCompanies(response.data.data || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
      setError(formatError(error) || t('companies:fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchUsersWithoutCompany = async () => {
    try {
      const response = await axios.get('/api/users/without-company');
      setUsersWithoutCompany(response.data.data || []);
    } catch (error) {
      console.error('Error fetching users without company:', error);
    }
  };

  const loadPendingInvitations = async (companyId, requestId) => {
    const response = await axios.get(`/api/companies/${companyId}/invitations`);
    if (requestId !== latestCompanyRequestRef.current) {
      return;
    }
    setPendingInvitations(response.data.data || []);
  };

  const fetchPendingInvitations = async (companyId) => {
    try {
      await loadPendingInvitations(companyId, latestCompanyRequestRef.current);
    } catch (error) {
      console.error('Error fetching pending invitations:', error);
    }
  };

  const fetchCompanyDetails = async (companyId) => {
    const requestId = ++latestCompanyRequestRef.current;
    setLoadingDetails(true);
    setSelectedCompany(companyId);
    try {
      const [usersResponse, tasksResponse, invitationsResponse] = await Promise.all([
        axios.get(`/api/companies/${companyId}/users`),
        axios.get(`/api/companies/${companyId}/tasks`),
        axios.get(`/api/companies/${companyId}/invitations`)
      ]);
      if (requestId !== latestCompanyRequestRef.current) {
        return;
      }
      setCompanyUsers(usersResponse.data.data || []);
      setTaskCounts(tasksResponse.data.data || { active_tasks: 0, done_tasks: 0 });
      setPendingInvitations(invitationsResponse.data.data || []);
    } catch (error) {
      if (requestId !== latestCompanyRequestRef.current) {
        return;
      }
      console.error('Error fetching company details:', error);
      setError(formatError(error) || t('superAdmin:fetchDetailsFailed'));
    } finally {
      if (requestId === latestCompanyRequestRef.current) {
        setLoadingDetails(false);
      }
    }
  };

  const handleCreateCompany = async (company) => {
    await fetchCompanies();
    setShowCreateCompanyModal(false);
  };

  const handleInviteUser = async (e) => {
    e.preventDefault();
    setInviteError('');
    setInviteSuccess('');

    if (!selectedCompany) {
      setInviteError(t('superAdmin:inviteSelectCompanyFirst'));
      return;
    }

    try {
      const response = await axios.post(`/api/companies/${selectedCompany}/invite`, {
        email: inviteEmail,
        role: inviteRole
      });

      if (response.data.success) {
        setInviteSuccess(t('company:inviteSent', { email: inviteEmail }));
        setInviteEmail('');
        setInviteRole('employee');
        await fetchPendingInvitations(selectedCompany);
        setTimeout(() => {
          setInviteSuccess('');
          setShowInviteModal(false);
        }, 2000);
      }
    } catch (error) {
      setInviteError(formatError(error) || t('company:failedInvite'));
    }
  };

  const handleRemoveUser = async (userId, userName) => {
    if (!(await confirm(t('superAdmin:removeUserConfirm', { name: userName }), { danger: true }))) {
      return;
    }

    try {
      await axios.delete(`/api/companies/${selectedCompany}/users/${userId}`);
      await fetchCompanyDetails(selectedCompany);
    } catch (error) {
      setError(formatError(error) || t('superAdmin:removeUserFailed'));
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`/api/companies/${selectedCompany}/users/${userId}/role`, {
        role: newRole
      });
      await fetchCompanyDetails(selectedCompany);
    } catch (error) {
      setError(formatError(error) || t('superAdmin:updateRoleFailed'));
    }
  };

  const handleOpenCompany = async (companyId) => {
    setOpeningSupport(companyId);
    setError('');
    try {
      await axios.post(`/api/companies/${companyId}/support`);
      await fetchUser();
      navigate('/dashboard');
    } catch (openError) {
      setError(formatError(openError) || t('superAdmin:openCompanyFailed'));
    } finally {
      setOpeningSupport(null);
    }
  };

  const handleDeleteCompany = async (companyId, companyName) => {
    if (!(await confirm(t('superAdmin:deleteCompanyConfirm', { name: companyName }), { danger: true }))) {
      return;
    }

    try {
      await axios.delete(`/api/companies/${companyId}`);
      if (selectedCompany === companyId) {
        setSelectedCompany(null);
        setCompanyUsers([]);
        setPendingInvitations([]);
        setTaskCounts({ active_tasks: 0, done_tasks: 0 });
      }
      await fetchCompanies();
    } catch (error) {
      setError(formatError(error) || t('companies:deleteFailed'));
    }
  };

  const getPricingPlanBadge = (plan) => {
    const plans = {
      basic: { label: t('superAdmin:planBasic'), className: 'super-admin-pricing-badge--basic' },
      professional: { label: t('superAdmin:planProfessional'), className: 'super-admin-pricing-badge--professional' },
      enterprise: { label: t('superAdmin:planEnterprise'), className: 'super-admin-pricing-badge--enterprise' }
    };
    const planInfo = plans[plan] || plans.basic;
    return (
      <span className={`super-admin-pricing-badge ${planInfo.className}`}>
        {planInfo.label}
      </span>
    );
  };

  const getSelectedCompany = () => companies.find((company) => company.id === selectedCompany) || null;

  const toDateInputValue = (iso) => {
    if (!iso) {
      return '';
    }
    return new Date(iso).toISOString().slice(0, 10);
  };

  const defaultTrialEndDate = () => {
    const date = new Date();
    date.setUTCDate(date.getUTCDate() + 14);
    return date.toISOString().slice(0, 10);
  };

  const syncTrialForm = (company) => {
    if (!company) {
      setTrialPlan('professional');
      setTrialEndsAt('');
      return;
    }

    setTrialPlan(company.trial_plan || 'professional');
    setTrialEndsAt(toDateInputValue(company.trial_ends_at) || defaultTrialEndDate());
    setTrialError('');
    setTrialSuccess('');
  };

  useEffect(() => {
    syncTrialForm(getSelectedCompany());
  }, [selectedCompany]);

  const getCompanyPlanSubtitle = (company) => {
    if (company.trial_active) {
      return t('superAdmin:trialUntilSubtitle', {
        date: formatTrialEndDate(company.trial_ends_at)
      });
    }

    if (company.subscription_status === 'past_due') {
      return t('superAdmin:pastDueSubtitle');
    }

    if (company.subscription_status === 'active' || company.subscription_status === 'trialing') {
      return t('superAdmin:subscribedSubtitle');
    }

    if (company.effective_plan !== company.pricing_plan) {
      return t('superAdmin:billingSubtitle', { plan: company.pricing_plan || 'basic' });
    }

    return null;
  };

  const handleSaveTrial = async (e) => {
    e.preventDefault();
    if (!selectedCompany) {
      return;
    }

    setTrialSaving(true);
    setTrialError('');
    setTrialSuccess('');

    try {
      const response = await axios.put(`/api/companies/${selectedCompany}/trial`, {
        trialPlan,
        trialEndsAt
      });
      setCompanies((current) =>
        current.map((company) =>
          company.id === selectedCompany ? { ...company, ...response.data.data } : company
        )
      );
      setTrialSuccess(t('superAdmin:trialSaved'));
    } catch (error) {
      setTrialError(formatError(error) || t('superAdmin:failedSaveTrial'));
    } finally {
      setTrialSaving(false);
    }
  };

  const handleClearTrial = async () => {
    if (!selectedCompany) {
      return;
    }

    if (!(await confirm(t('superAdmin:confirmClearTrial'), { danger: true }))) {
      return;
    }

    setTrialSaving(true);
    setTrialError('');
    setTrialSuccess('');

    try {
      const response = await axios.put(`/api/companies/${selectedCompany}/trial`, {
        trialPlan: null
      });
      setCompanies((current) =>
        current.map((company) =>
          company.id === selectedCompany ? { ...company, ...response.data.data } : company
        )
      );
      setTrialSuccess(t('superAdmin:trialCleared'));
    } catch (error) {
      setTrialError(formatError(error) || t('superAdmin:failedClearTrial'));
    } finally {
      setTrialSaving(false);
    }
  };

  const handleSavePlatformSettings = async (event) => {
    event.preventDefault();
    setPlatformSettingsSaving(true);
    setPlatformSettingsError('');
    setPlatformSettingsSuccess('');

    try {
      const response = await axios.put('/api/platform/settings/support-slack-webhook', {
        webhookUrl: supportWebhookUrl.trim() || null
      });
      const data = response.data.data || {};
      setSupportWebhookUrl(data.supportSlackWebhookOverride || '');
      setSupportWebhookConfigured(Boolean(data.supportSlackWebhookConfigured));
      setSupportWebhookUsesEnv(Boolean(data.supportSlackWebhookUsesEnvDefault));
      setPlatformSettingsSuccess(t('superAdmin:platformSettingsSaved'));
    } catch (settingsError) {
      setPlatformSettingsError(formatError(settingsError) || t('superAdmin:failedSavePlatformSettings'));
    } finally {
      setPlatformSettingsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="super-admin-view" aria-busy="true">
        <div className="container container--wide main-content">
          <LoadingSpinner className="super-admin-loading" />
        </div>
      </div>
    );
  }

  return (
    <div className="super-admin-view">
      <div className="container container--wide main-content">
        <div className="super-admin-container">
          <h1 className="super-admin-title">{t('superAdmin:title')}</h1>
          <p className="super-admin-subtitle">{t('superAdmin:subtitle')}</p>

          {error && <div className="error">{error}</div>}

          <div className="super-admin-details-card super-admin-platform-settings">
            <h3 className="super-admin-details-title">{t('superAdmin:platformSettingsTitle')}</h3>
            <form className="super-admin-trial-form" onSubmit={handleSavePlatformSettings}>
              <div className="form-group">
                <label htmlFor="supportWebhookUrl">{t('superAdmin:supportWebhookLabel')}</label>
                <input
                  id="supportWebhookUrl"
                  type="url"
                  value={supportWebhookUrl}
                  onChange={(event) => setSupportWebhookUrl(event.target.value)}
                  placeholder={t('superAdmin:supportWebhookPlaceholder')}
                  disabled={platformSettingsSaving}
                />
                <p className="super-admin-company-meta">
                  {t('superAdmin:supportWebhookHelp')}
                </p>
                <p className="super-admin-company-meta">
                  {!supportWebhookConfigured && t('superAdmin:supportWebhookNotConfigured')}
                  {supportWebhookConfigured && supportWebhookUrl.trim() && t('superAdmin:supportWebhookOverrideActive')}
                  {supportWebhookConfigured && !supportWebhookUrl.trim() && supportWebhookUsesEnv && t('superAdmin:supportWebhookEnvDefault')}
                </p>
              </div>
              {platformSettingsError && <div className="error">{platformSettingsError}</div>}
              {platformSettingsSuccess && <div className="success">{platformSettingsSuccess}</div>}
              <button type="submit" className="btn btn-primary btn-sm" disabled={platformSettingsSaving}>
                {platformSettingsSaving ? t('superAdmin:savingTrial') : t('superAdmin:savePlatformSettings')}
              </button>
            </form>
          </div>

          <div className="super-admin-grid">
            <div className="super-admin-companies-section">
              <div className="super-admin-section-header">
                <h2 className="super-admin-section-title">
                  {t('superAdmin:usersWithoutCompanyTitle', { count: usersWithoutCompany.length })}
                </h2>
              </div>
              <div className="super-admin-companies-list">
                {usersWithoutCompany.length === 0 ? (
                  <EmptyState
                    icon="person_off"
                    title={t('superAdmin:usersWithoutCompanyEmptyTitle')}
                    description={t('superAdmin:usersWithoutCompanyEmptyDescription')}
                  />
                ) : (
                  usersWithoutCompany.map((userWithoutCompany) => (
                    <div key={userWithoutCompany.id} className="super-admin-company-card">
                      <div className="super-admin-company-card-content">
                        <div className="super-admin-company-card-header">
                          <div className="super-admin-user-row">
                            <UserAvatar user={userWithoutCompany} size={32} />
                            <div>
                              <h3 className="super-admin-company-name">
                                {userWithoutCompany.first_name} {userWithoutCompany.last_name}
                              </h3>
                              <p className="super-admin-company-meta">{userWithoutCompany.email}</p>
                            </div>
                          </div>
                        </div>
                        {userWithoutCompany.phone && (
                          <p className="super-admin-company-meta">
                            {t('superAdmin:phoneLabel', { phone: userWithoutCompany.phone })}
                          </p>
                        )}
                        <p className="super-admin-company-meta">
                          {t('superAdmin:roleStatusLabel', {
                            role: formatRoleLabel(userWithoutCompany.role, t),
                            status: userWithoutCompany.is_active
                              ? t('common:active')
                              : t('common:inactive')
                          })}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="super-admin-companies-section">
              <div className="super-admin-section-header">
                <h2 className="super-admin-section-title">
                  {t('superAdmin:allCompaniesTitle', { count: companies.length })}
                </h2>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => setShowCreateCompanyModal(true)}
                >
                  {t('companies:createCompany')}
                </button>
              </div>
              <div className="super-admin-companies-list">
                {companies.length === 0 ? (
                  <EmptyState
                    icon="business"
                    title={t('superAdmin:allCompaniesEmptyTitle')}
                    description={t('superAdmin:allCompaniesEmptyDescription')}
                    actionLabel={t('companies:createCompany')}
                    onAction={() => setShowCreateCompanyModal(true)}
                  />
                ) : (
                  companies.map((company) => (
                    <div
                      key={company.id}
                      className={`super-admin-company-card ${
                        selectedCompany === company.id ? 'active' : ''
                      }`}
                    >
                      <div
                        className="super-admin-company-card-content"
                        onClick={() => fetchCompanyDetails(company.id)}
                      >
                        <div className="super-admin-company-card-header">
                          <h3 className="super-admin-company-name">{company.name}</h3>
                          {getPricingPlanBadge(company.effective_plan || company.pricing_plan || 'basic')}
                        </div>
                        {getCompanyPlanSubtitle(company) && (
                          <p className="super-admin-company-plan-meta">{getCompanyPlanSubtitle(company)}</p>
                        )}
                        {company.email && (
                          <p className="super-admin-company-meta">{company.email}</p>
                        )}
                        {company.address && (
                          <p className="super-admin-company-meta">{company.address}</p>
                        )}
                        <p className="super-admin-company-meta">
                          {t('superAdmin:userCount', { count: company.user_count || 0 })}
                        </p>
                      </div>
                      <div className="super-admin-company-actions">
                        <button
                          type="button"
                          className="btn btn-sm btn-primary super-admin-company-open"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenCompany(company.id);
                          }}
                          disabled={openingSupport === company.id}
                          title={t('superAdmin:openCompanyTitle')}
                        >
                          {openingSupport === company.id
                            ? t('superAdmin:openingCompany')
                            : t('superAdmin:openCompany')}
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger super-admin-company-delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCompany(company.id, company.name);
                          }}
                          disabled={selectedCompany !== company.id}
                          title={
                            selectedCompany === company.id
                              ? t('superAdmin:deleteCompanyTitle')
                              : t('superAdmin:selectCompanyToDelete')
                          }
                        >
                          <span className="material-symbols-outlined">delete</span>
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {selectedCompany && (
              <div className="super-admin-details-section">
                {loadingDetails ? (
                  <LoadingSpinner className="super-admin-loading" />
                ) : (
                  <>
                    <div className="super-admin-details-card">
                      <h3 className="super-admin-details-title">{t('superAdmin:planTrialTitle')}</h3>
                      {(() => {
                        const company = getSelectedCompany();
                        if (!company) {
                          return null;
                        }

                        return (
                          <>
                            <div className="super-admin-plan-summary">
                              <div>
                                <strong>{t('superAdmin:effectiveTier')}</strong>{' '}
                                {getPricingPlanBadge(company.effective_plan || company.pricing_plan || 'basic')}
                              </div>
                              <div>
                                <strong>{t('superAdmin:billingTier')}</strong> {company.pricing_plan || 'basic'}
                              </div>
                              {company.trial_active && (
                                <div>
                                  {t('superAdmin:trialUntil', {
                                    plan: company.trial_plan,
                                    date: formatTrialEndDate(company.trial_ends_at)
                                  })}
                                </div>
                              )}
                              {company.subscription_status && (
                                <div>
                                  {t('superAdmin:subscriptionStatus', { status: company.subscription_status })}
                                </div>
                              )}
                            </div>

                            <form className="super-admin-trial-form" onSubmit={handleSaveTrial}>
                              <div className="form-group">
                                <label htmlFor="trialPlan">{t('superAdmin:trialTierLabel')}</label>
                                <select
                                  id="trialPlan"
                                  className="super-admin-role-select"
                                  value={trialPlan}
                                  onChange={(e) => setTrialPlan(e.target.value)}
                                  disabled={trialSaving}
                                >
                                  <option value="professional">{t('superAdmin:planProfessional')}</option>
                                  <option value="enterprise">{t('superAdmin:planEnterprise')}</option>
                                </select>
                              </div>
                              <div className="form-group">
                                <label htmlFor="trialEndsAt">{t('superAdmin:trialEndDateLabel')}</label>
                                <input
                                  id="trialEndsAt"
                                  type="date"
                                  value={trialEndsAt}
                                  onChange={(e) => setTrialEndsAt(e.target.value)}
                                  disabled={trialSaving}
                                  required
                                />
                              </div>
                              {trialError && <div className="error">{trialError}</div>}
                              {trialSuccess && <div className="success">{trialSuccess}</div>}
                              <div className="super-admin-modal-actions">
                                <button
                                  type="button"
                                  className="btn btn-secondary btn-sm"
                                  onClick={handleClearTrial}
                                  disabled={trialSaving || !company.trial_plan}
                                >
                                  {t('superAdmin:clearTrial')}
                                </button>
                                <button
                                  type="submit"
                                  className="btn btn-primary btn-sm"
                                  disabled={trialSaving || !trialEndsAt}
                                >
                                  {trialSaving ? t('superAdmin:savingTrial') : t('superAdmin:saveTrial')}
                                </button>
                              </div>
                            </form>
                          </>
                        );
                      })()}
                    </div>

                    <div className="super-admin-details-card">
                      <h3 className="super-admin-details-title">
                        {t('superAdmin:pendingInvitationsTitle', { count: pendingInvitations.length })}
                      </h3>
                      <PendingInvitationsTable
                        companyId={selectedCompany}
                        invitations={pendingInvitations}
                        onRefresh={() => fetchPendingInvitations(selectedCompany)}
                        onCancelSuccess={() => setError('')}
                        onCancelError={(message) => setError(message)}
                        cancelLabel={t('superAdmin:removeInvitation')}
                        cancellingLabel={t('superAdmin:removingInvitation')}
                        confirmMessage={t('superAdmin:removeInvitationConfirm')}
                        emptyClassName="super-admin-empty"
                      />
                    </div>

                    <div className="super-admin-details-card">
                      <div className="super-admin-details-header">
                        <h3 className="super-admin-details-title">
                          {t('superAdmin:usersTitle', { count: companyUsers.length })}
                        </h3>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => {
                            setShowInviteModal(true);
                            setInviteError('');
                            setInviteSuccess('');
                          }}
                        >
                          {t('superAdmin:inviteUser')}
                        </button>
                      </div>
                      {companyUsers.length === 0 ? (
                        <EmptyState
                          icon="group"
                          title={t('company:noCompanyUsersTitle')}
                          description={t('superAdmin:usersEmptyDescription')}
                          actionLabel={t('superAdmin:inviteUser')}
                          onAction={() => {
                            setShowInviteModal(true);
                            setInviteError('');
                            setInviteSuccess('');
                          }}
                        />
                      ) : (
                        <div className="super-admin-users-list">
                          {companyUsers.map((companyUser) => (
                            <div key={companyUser.id} className="super-admin-user-item">
                              <UserAvatar user={companyUser} size={32} />
                              <div className="super-admin-user-info">
                                <div className="super-admin-user-name">
                                  {companyUser.first_name} {companyUser.last_name}
                                </div>
                                <div className="super-admin-user-meta">
                                  {companyUser.email}
                                </div>
                              </div>
                              <div className="super-admin-user-actions">
                                <select
                                  className="super-admin-role-select"
                                  value={companyUser.role}
                                  onChange={(e) => handleRoleChange(companyUser.id, e.target.value)}
                                >
                                  <option value="admin">{formatRoleLabel('admin', t)}</option>
                                  <option value="employee">{formatRoleLabel('employee', t)}</option>
                                  <option value="reader">{formatRoleLabel('reader', t)}</option>
                                </select>
                                <button
                                  className="btn btn-sm btn-danger"
                                  onClick={() => handleRemoveUser(
                                    companyUser.id,
                                    `${companyUser.first_name} ${companyUser.last_name}`.trim() || companyUser.email
                                  )}
                                  disabled={!selectedCompany}
                                  title={
                                    selectedCompany
                                      ? t('superAdmin:removeUserTitle')
                                      : t('superAdmin:selectCompanyFirst')
                                  }
                                >
                                  <span className="material-symbols-outlined">delete</span>
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="super-admin-details-card">
                      <h3 className="super-admin-details-title">{t('superAdmin:taskStatisticsTitle')}</h3>
                      <div className="super-admin-task-stats">
                        <div className="super-admin-task-stat-item">
                          <div className="super-admin-task-stat-label">{t('superAdmin:activeTasks')}</div>
                          <div className="super-admin-task-stat-number">{taskCounts.active_tasks || 0}</div>
                        </div>
                        <div className="super-admin-task-stat-item">
                          <div className="super-admin-task-stat-label">{t('superAdmin:doneTasks')}</div>
                          <div className="super-admin-task-stat-number">{taskCounts.done_tasks || 0}</div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <CreateCompanyModal
        isOpen={showCreateCompanyModal}
        onClose={() => setShowCreateCompanyModal(false)}
        onSuccess={handleCreateCompany}
        defaultPricingPlan="basic"
      />

      <Modal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        title={t('company:inviteTitle')}
      >
        <form onSubmit={handleInviteUser} className="super-admin-invite-form">
          {inviteError && <div className="error">{inviteError}</div>}
          {inviteSuccess && <div className="success">{inviteSuccess}</div>}

          <div className="form-group">
            <label htmlFor="inviteEmail">{t('company:inviteEmailLabel')}</label>
            <input
              type="email"
              id="inviteEmail"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              required
              placeholder={t('company:inviteEmailPlaceholder')}
            />
          </div>

          <div className="form-group">
            <label htmlFor="inviteRole">{t('common:role')} *</label>
            <select
              id="inviteRole"
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value)}
              required
            >
              <option value="admin">{formatRoleLabel('admin', t)}</option>
              <option value="employee">{formatRoleLabel('employee', t)}</option>
              <option value="reader">{formatRoleLabel('reader', t)}</option>
            </select>
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowInviteModal(false)}
            >
              {t('common:cancel')}
            </button>
            <button type="submit" className="btn btn-primary">
              {t('company:sendInvitation')}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default SuperAdminView;
